// ---------------------------------------------------------------------------
// Imports a project from a folder that has NO .ehd — just the CSV files
// EvalHid_cal itself reads (the same ones exportCsvProject.ts writes back
// out, see its own header comment for the format). Entry point for "Open
// project from folder" when listEhdFiles() finds nothing but the folder
// still looks like an EvalHid working directory.
//
// Always ends up with `gisLayer: null` (the CSV format carries no
// geometry) — appState.ts's own `mapView: gisLayer !== null && ...` already
// makes that show up as card view (SubBasinCanvas) automatically, no
// special-casing needed here.
//
// What CAN'T survive a CSV-only round trip, and how it's handled instead of
// refused:
//   - A zone's internal numeric code doesn't survive — exportCsvProject.ts
//     writes zones by NAME (Zona_parametros/Zona_parametros_nieve in
//     Ent_Cuencas.csv, and each Ent_{modelo}_Param.csv's own first column),
//     deliberately, so the name itself DOES survive. Each zone found gets a
//     fresh sequential code here, only meaningful within this import. Two
//     zones of the same kind (both runoff, or both snow) sharing a literal
//     name are ambiguous — the later one silently wins in the name→code
//     lookup — but exportCsvProject.ts's own informe.txt already flags that
//     as a finding at export time, so a folder this app produced won't have
//     that problem to begin with.
//   - Parameter/state-variable IDENTITY: a real .ehd-exported CSV's column
//     headers can use different short names than this app's own catalog
//     (baseTemplate.ts) — confirmed live, a real project's Témez state
//     variables were named "H"/"Vi" instead of "Ho"/"Vo". Matching by name
//     would silently drop those columns, so columns are matched
//     POSITIONALLY instead, against baseTemplate.ts's own definitions
//     sorted the same way the exporter orders them (sortedByCode) — correct
//     for a file this app (or the original, using the same definition
//     catalog) produced, since the column COUNT and ORDER always come from
//     that same catalog either way.
// ---------------------------------------------------------------------------
import { readArrayBuffer } from "./project.ts";
import { parseDailySeriesAsync } from "./csvWorkerClient.ts";
import { resolveIndicesByName, type DailySeries } from "./dailySeries.ts";
import { detectScale } from "./seriesScale.ts";
import { MODEL_TYPES } from "./baseTemplate.ts";
import {
  DEFAULT_PATHS,
  DEFAULT_SERIES_PATHS,
  LABEL_AQUIFERS,
  LABEL_OUTLET_POINTS,
  LABEL_CONFIG,
  LABEL_SUBBASINS,
  paramFileName,
} from "./csvProjectFileNames.ts";
import {
  csvHeaderIndex,
  numberOr,
  parseAquiferCsvFields,
  parseAquiferOptionFlags,
  parseConfigValues,
  parseOutletPointName,
  parseSimpleCsv,
  parseSubBasinCsvFields,
  parseZoneParamRow,
  readCsvText,
  unitOptionsFromConfigValues,
  zoneParamDefsFor,
} from "./csvProjectParsing.ts";
import { emptyProject } from "./projectEntities.ts";
import { DEFAULT_UNIT_OPTIONS } from "./units.ts";
import type { LoadedProject } from "./project.ts";
import type { ReadableFolder } from "./fsReadable.ts";
import type {
  Aquifer,
  EvalHidProject,
  OutletPoint,
  ParameterValue,
  StateVariableValue,
  SubBasin,
  UnitOption,
  Zone,
} from "./types.ts";
import { TranslatableError, type Translate } from "../i18n/translate.ts";
import type { Language } from "./language.ts";

/** Whether this folder looks like a CSV-only EvalHid project — the one file always written regardless of how many sub-basins/zones/aquifers exist (see exportCsvProject.ts). Checked before falling back to this importer, so a folder with neither a .ehd nor this file still gets the normal "no project found" error. */
export async function hasCsvProjectFiles(folder: ReadableFolder): Promise<boolean> {
  try {
    await folder.getFileHandle(DEFAULT_PATHS[LABEL_SUBBASINS]);
    return true;
  } catch {
    return false;
  }
}

interface ParsedZones {
  zones: Zone[];
  parameterValues: ParameterValue[];
  stateVariableValues: StateVariableValue[];
  /** Zone name -> freshly-assigned zoneCode, one map per kind (see this file's own header on why they're kept separate). */
  runoffZoneCodeByName: Map<string, number>;
  snowZoneCodeByName: Map<string, number>;
}

/** Reads every `Ent_{modelo}_Param.csv` present in the folder — one call per model in the catalog, since that's the original's own write granularity (see EscribeCsvParametros). Missing files just mean no zone uses that model; not an error. */
async function readZonesFromParamFiles(folder: ReadableFolder): Promise<ParsedZones> {
  const zones: Zone[] = [];
  const parameterValues: ParameterValue[] = [];
  const stateVariableValues: StateVariableValue[] = [];
  const runoffZoneCodeByName = new Map<string, number>();
  const snowZoneCodeByName = new Map<string, number>();
  let nextZoneCode = 1;

  for (const model of MODEL_TYPES) {
    const text = await readCsvText(folder, paramFileName(model.modelTypeName));
    if (text == null) continue;

    const { paramDefs, stateDefs } = zoneParamDefsFor(model);

    for (const cols of parseSimpleCsv(text)) {
      const name = cols[0];
      if (!name) continue;
      const zoneCode = nextZoneCode++;
      (model.kind === 1 ? snowZoneCodeByName : runoffZoneCodeByName).set(name, zoneCode);

      zones.push({
        zoneCode,
        name,
        modelTypeCode: model.kind === 0 ? model.modelTypeCode : null,
        snowModelTypeCode: model.kind === 1 ? model.modelTypeCode : null,
      });

      const row = parseZoneParamRow(cols, zoneCode, model.kind, paramDefs, stateDefs);
      parameterValues.push(...row.parameterValues);
      stateVariableValues.push(...row.stateVariableValues);
    }
  }

  return { zones, parameterValues, stateVariableValues, runoffZoneCodeByName, snowZoneCodeByName };
}

function readOutletPoints(text: string | null): OutletPoint[] {
  if (text == null) return [];
  return parseSimpleCsv(text).map((cols, i) => ({
    outletCode: i + 1,
    name: parseOutletPointName(cols),
    gisCode: null,
    aquatoolCode: null,
  }));
}

function readAquifers(text: string | null, outletCodeByName: Map<string, number>): Aquifer[] {
  if (text == null) return [];
  return parseSimpleCsv(text).map((cols, i) => {
    const f = parseAquiferCsvFields(cols, outletCodeByName);
    return {
      aquiferCode: i + 1,
      name: f.name,
      gisCode: null,
      aquatoolCode: null,
      outletCode: f.outletCode,
      vo: f.vo,
      alfa: f.alfa,
      pumpingCol: 0,
    };
  });
}

function readSubBasins(
  text: string,
  outletCodeByName: Map<string, number>,
  aquiferCodeByName: Map<string, number>,
  zonesByCode: Map<number, Zone>,
  runoffZoneCodeByName: Map<string, number>,
  snowZoneCodeByName: Map<string, number>
): SubBasin[] {
  const columnIndex = csvHeaderIndex(text);
  return parseSimpleCsv(text).map((cols, i) => {
    const f = parseSubBasinCsvFields(
      cols,
      columnIndex,
      outletCodeByName,
      aquiferCodeByName,
      runoffZoneCodeByName,
      snowZoneCodeByName,
      zonesByCode
    );
    return {
      subBasinCode: i + 1,
      area: numberOr(f.areaRaw, 0),
      precipCol: 0,
      etpCol: 0,
      tempCol: 0,
      pumpingCol: 0,
      partialResults: numberOr(f.partialResultsRaw, 0),
      precipAdjustment: numberOr(f.precipAdjustmentRaw, 1),
      etpAdjustment: numberOr(f.etpAdjustmentRaw, 1),
      tempAdjustment: numberOr(f.tempAdjustmentRaw, 1),
      modelTypeCode: f.modelTypeCode,
      outletCode: f.outletCode || null,
      aquiferCode: f.aquiferCode,
      snowModelTypeCode: f.snowModelTypeCode,
      gisCode: null,
      description: f.name || String(i + 1),
      zoneCode: f.zoneCode,
      snowZoneCode: f.snowZoneCode,
    };
  });
}

function readConfig(text: string | null): {
  unitOptions: UnitOption[];
  aquiferOpc: Record<string, unknown>[];
} {
  if (text == null) {
    return { unitOptions: DEFAULT_UNIT_OPTIONS, aquiferOpc: [] };
  }
  const values = parseConfigValues(text);
  const unitOptions = unitOptionsFromConfigValues(values, DEFAULT_UNIT_OPTIONS);
  const flags = parseAquiferOptionFlags(values);
  const aquiferOpc = [
    { Codigo: 1, Checked: flags.negatives ?? false },
    { Codigo: 2, Checked: flags.pumping ?? false },
  ];
  return { unitOptions, aquiferOpc };
}

/** Links entities to a series' columns by name (resolveIndicesByName) only when the series was actually found — same criteria project.ts's own resolveIfUnlinked uses. */
function linkColumn<T>(
  entities: T[],
  series: DailySeries | null,
  nameOf: (e: T) => string,
  withIndex: (e: T, index: number) => T
): T[] {
  return series ? resolveIndicesByName(entities, series, nameOf, withIndex) : entities;
}

/** Same 4 optional series a .ehd-based project reads (project.ts's own openProject) — same file names too, hardcoded here since there's no APO_ARCHIVOS_SIMULACION to read a custom one from without a .ehd. */
async function readOptionalSeries(
  folder: ReadableFolder,
  fileName: string,
  language: Language
): Promise<DailySeries | null> {
  try {
    const bytes = await readArrayBuffer(folder, fileName);
    return await parseDailySeriesAsync(bytes, language);
  } catch {
    return null;
  }
}

/** Imports a project from a folder with no .ehd — see this file's own header for what a CSV-only round trip can and can't preserve. */
export async function openCsvProject(
  t: Translate,
  language: Language,
  folder: ReadableFolder
): Promise<LoadedProject> {
  const notices: string[] = [];

  const subBasinsText = await readCsvText(folder, DEFAULT_PATHS[LABEL_SUBBASINS]);
  if (subBasinsText == null) {
    throw new TranslatableError("importCsvProject.cuencasNotFoundError");
  }

  const outletPointsText = await readCsvText(folder, DEFAULT_PATHS[LABEL_OUTLET_POINTS]);
  const outletPoints = readOutletPoints(outletPointsText);
  const outletCodeByName = new Map(outletPoints.map((p) => [p.name, p.outletCode]));

  const aquifersText = await readCsvText(folder, DEFAULT_PATHS[LABEL_AQUIFERS]);
  const aquifers = readAquifers(aquifersText, outletCodeByName);
  const aquiferCodeByName = new Map(aquifers.map((a) => [a.name, a.aquiferCode]));

  const { zones, parameterValues, stateVariableValues, runoffZoneCodeByName, snowZoneCodeByName } =
    await readZonesFromParamFiles(folder);
  const zonesByCode = new Map(zones.map((z) => [z.zoneCode, z]));

  const subBasins = readSubBasins(
    subBasinsText,
    outletCodeByName,
    aquiferCodeByName,
    zonesByCode,
    runoffZoneCodeByName,
    snowZoneCodeByName
  );

  const configText = await readCsvText(folder, DEFAULT_PATHS[LABEL_CONFIG]);
  const { unitOptions, aquiferOpc } = readConfig(configText);
  if (configText == null) notices.push(t("importCsvProject.noConfigWarning"));

  const [precipitation, etp, temperature, pumping] = await Promise.all([
    readOptionalSeries(folder, DEFAULT_SERIES_PATHS.precipitation, language),
    readOptionalSeries(folder, DEFAULT_SERIES_PATHS.etp, language),
    readOptionalSeries(folder, DEFAULT_SERIES_PATHS.temperature, language),
    readOptionalSeries(folder, DEFAULT_SERIES_PATHS.pumping, language),
  ]);
  if (!precipitation) notices.push(t("importCsvProject.noPrecipitationWarning"));
  if (!etp) notices.push(t("importCsvProject.noEtpWarning"));

  let linkedSubBasins = subBasins;
  for (const [series, column] of [
    [precipitation, "precipCol"],
    [etp, "etpCol"],
    [temperature, "tempCol"],
    // Resolved by SUB-BASIN name, independent of the aquifer resolution
    // below (same shared file/series, see SubBasin.pumpingCol's own doc
    // comment) — a sub-basin doesn't inherit its aquifer's column.
    [pumping, "pumpingCol"],
  ] as const) {
    linkedSubBasins = linkColumn(
      linkedSubBasins,
      series,
      (c) => c.description,
      (c, i) => ({ ...c, [column]: i })
    );
  }
  const linkedAquifers = linkColumn(
    aquifers,
    pumping,
    (a) => a.name,
    (a, i) => ({
      ...a,
      pumpingCol: i,
    })
  );

  const project: EvalHidProject = {
    ...emptyProject(),
    subBasins: linkedSubBasins,
    aquifers: linkedAquifers,
    outletPoints,
    zones,
    parameterValues,
    stateVariableValues,
    unitOptions,
    // No Escala_Temporal read here (parseConfigValues doesn't look at that
    // key — see seriesScale.ts's own header) — detected from the real
    // precipitation dates instead, same bootstrap loadEhd/project.ts use.
    seriesScale: precipitation ? detectScale(precipitation.dates) : "daily",
    untypedTables: new Map([["APO_ACUIFEROS_OPC", aquiferOpc]]),
  };

  return {
    project,
    series: { precipitation, etp, temperature, pumping },
    notices,
  };
}
